import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserCheck, Plus, Trash2, KeyRound, Copy, Check, ShieldCheck, Mail, Calendar } from "lucide-react";

export function AdminEventHandlers() {
  const [staffList, setStaffList] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  // Form state
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [assignedEvents, setAssignedEvents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [staffRes, eventsRes] = await Promise.all([
        api.get("/admin/staff"),
        api.get("/admin/events"),
      ]);
      setStaffList(staffRes.data);
      setEvents(eventsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load staff list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$";
    let p = "";
    for (let i = 0; i < 10; i++) {
      p += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(p);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      return toast.error("Email and temporary password are required");
    }
    if (password.length < 6) {
      return toast.error("Password must be at least 6 characters");
    }
    setSubmitting(true);
    try {
      await api.post("/admin/staff", {
        email,
        name: name || undefined,
        password,
        role: "check_in",
        assigned_events: assignedEvents,
      });
      toast.success(`Event handler ${email} created successfully`);
      setEmail("");
      setName("");
      setPassword("");
      setAssignedEvents([]);
      setModalOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create handler");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await api.put(`/admin/staff/${id}/toggle`);
      toast.success(res.data.active ? "Handler activated" : "Handler deactivated");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const handleDelete = async (id, staffEmail) => {
    if (!window.confirm(`Are you sure you want to remove handler ${staffEmail}?`)) return;
    try {
      await api.delete(`/admin/staff/${id}`);
      toast.success("Handler removed");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete handler");
    }
  };

  const copyLoginLink = (item) => {
    const url = window.location.origin + "/admin/login";
    navigator.clipboard.writeText(url);
    setCopiedId(item.id);
    toast.success("Login link copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const eventHandlers = staffList.filter((s) => s.role === "check_in");
  const otherStaff = staffList.filter((s) => s.role !== "check_in");

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="eyebrow flex items-center gap-1.5">
            <UserCheck size={14} /> Entry Management
          </div>
          <h1 className="font-display text-3xl font-bold mt-1">Event Handlers</h1>
          <p className="text-sm text-stone-500 mt-1">
            Accounts with restricted access exclusively to scan attendee QR tickets and mark entry at events.
          </p>
        </div>

        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogTrigger asChild>
            <Button className="btn-brand" data-testid="btn-add-handler">
              <Plus size={16} className="mr-1.5" /> Add Event Handler
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck size={18} /> New Event Handler
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div>
                <Label className="text-xs">Handler Email *</Label>
                <Input
                  type="email"
                  placeholder="handler@cgcollective.in"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1"
                  data-testid="input-handler-email"
                />
              </div>

              <div>
                <Label className="text-xs">Full Name / Gate Identifier (Optional)</Label>
                <Input
                  placeholder="e.g. Rahul · Gate 1 Scanner"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1"
                  data-testid="input-handler-name"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Temporary Password *</Label>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="text-[11px] text-[color:var(--brand-terracotta)] hover:underline flex items-center gap-1 font-medium"
                  >
                    <KeyRound size={12} /> Auto-generate
                  </button>
                </div>
                <Input
                  type="text"
                  placeholder="Min 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 font-mono text-sm"
                  data-testid="input-handler-password"
                />
                <p className="text-[11px] text-stone-500 mt-1">
                  Share this temporary password with the handler. They will log in at <code>/admin/login</code>.
                </p>
              </div>

              <div>
                <Label className="text-xs">Assigned Events (Optional)</Label>
                <select
                  multiple
                  value={assignedEvents}
                  onChange={(e) => setAssignedEvents(Array.from(e.target.selectedOptions, (o) => o.value))}
                  className="w-full border border-border rounded-md p-2 text-xs bg-background mt-1 h-24"
                >
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>
                      {ev.title} ({ev.city})
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-stone-500 mt-0.5">
                  Hold Ctrl/Cmd to select multiple events, or leave unselected for all events.
                </p>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="btn-brand" data-testid="submit-add-handler">
                  {submitting ? "Creating…" : "Create & Send Access"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Event Handlers Section */}
      <div className="cgc-card overflow-hidden">
        <div className="p-4 border-b border-border bg-stone-50/50 dark:bg-stone-900/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ScanLine size={16} className="text-[color:var(--brand-terracotta)]" />
            <h2 className="font-semibold text-sm">Active Event Handlers ({eventHandlers.length})</h2>
          </div>
          <span className="text-xs text-stone-500">Only have access to QR Scanner</span>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-stone-500">Loading handlers…</div>
        ) : eventHandlers.length === 0 ? (
          <div className="p-8 text-center text-stone-500 text-sm">
            No dedicated event handlers added yet. Click <b>"Add Event Handler"</b> to create scanner accounts for volunteers or staff at the venue.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-stone-100/60 dark:bg-stone-900/60 text-xs font-mono uppercase text-stone-500 border-b border-border">
                <tr>
                  <th className="p-3.5">Handler</th>
                  <th className="p-3.5">Role</th>
                  <th className="p-3.5">Assigned Events</th>
                  <th className="p-3.5">Created</th>
                  <th className="p-3.5">Status</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {eventHandlers.map((h) => (
                  <tr key={h.id} className="hover:bg-stone-50/50 dark:hover:bg-stone-900/30">
                    <td className="p-3.5">
                      <div className="font-semibold">{h.name || "Event Handler"}</div>
                      <div className="text-xs font-mono text-stone-500">{h.email}</div>
                    </td>
                    <td className="p-3.5">
                      <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200 font-mono">
                        Ticket Scanner
                      </span>
                    </td>
                    <td className="p-3.5 text-xs text-stone-600 dark:text-stone-400">
                      {h.assigned_events?.length ? (
                        <span>{h.assigned_events.length} event(s) assigned</span>
                      ) : (
                        <span className="text-stone-400">All events permitted</span>
                      )}
                    </td>
                    <td className="p-3.5 text-xs text-stone-500">{fmtDate(h.created_at)}</td>
                    <td className="p-3.5">
                      <button
                        onClick={() => handleToggle(h.id)}
                        className={`px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors ${
                          h.active
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                            : "bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-400"
                        }`}
                        title="Click to toggle status"
                      >
                        {h.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="p-3.5 text-right space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyLoginLink(h)}
                        title="Copy login link"
                        className="h-8 px-2"
                      >
                        {copiedId === h.id ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(h.id, h.email)}
                        className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                        title="Remove handler"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Admin / Higher Privilege Accounts Section */}
      {otherStaff.length > 0 && (
        <div className="cgc-card overflow-hidden">
          <div className="p-4 border-b border-border bg-stone-50/50 dark:bg-stone-900/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-stone-600" />
              <h2 className="font-semibold text-sm">Administrative Staff ({otherStaff.length})</h2>
            </div>
            <span className="text-xs text-stone-500">Super Admins & Managers</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-stone-100/60 dark:bg-stone-900/60 text-xs font-mono uppercase text-stone-500 border-b border-border">
                <tr>
                  <th className="p-3.5">Name</th>
                  <th className="p-3.5">Email</th>
                  <th className="p-3.5">Role</th>
                  <th className="p-3.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {otherStaff.map((s) => (
                  <tr key={s.id} className="hover:bg-stone-50/50 dark:hover:bg-stone-900/30">
                    <td className="p-3.5 font-medium">{s.name}</td>
                    <td className="p-3.5 font-mono text-xs">{s.email}</td>
                    <td className="p-3.5">
                      <span className="px-2 py-0.5 rounded text-xs bg-stone-200 text-stone-800 dark:bg-stone-800 dark:text-stone-300 font-mono capitalize">
                        {s.role.replace("_", " ")}
                      </span>
                    </td>
                    <td className="p-3.5">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.active ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300" : "bg-stone-200 text-stone-700"}`}>
                        {s.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ScanLine({ size = 16, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M3 17v2a2 2 0 0 0 2 2h2" />
      <line x1="7" x2="17" y1="12" y2="12" />
    </svg>
  );
}
